<template>
  <div>
    <b-card no-body>
      <b-card-header class="pb-50">
        <h5 class="mb-1">Filtros</h5>
      </b-card-header>
      <b-card-body>
        <b-row>
          <b-col cols="12" md="4" class="mb-md-0 mb-2">
            <b-form-group label="Relatório" label-for="salesOpsType">
              <b-form-select
                id="salesOpsType"
                v-model="type"
                :options="reportTypes"
                @change="search(1)"
              />
            </b-form-group>
          </b-col>
          <b-col cols="12" md="6" class="mb-md-0 mb-2">
            <b-form-group label="Período" label-for="salesOpsPeriod">
              <period-range-picker
                id="salesOpsPeriod"
                v-model="period"
                :disabled="loading.search"
                @change="search(1)"
              />
            </b-form-group>
          </b-col>
          <b-col cols="12" class="mb-md-0 mb-2 text-right">
            <b-button
              variant="outline-primary"
              :disabled="loading.search"
              @click.prevent="search(1)"
            >
              <b-spinner v-if="loading.search" small />
              <feather-icon v-else icon="SearchIcon" class="mr-50" />
              <span>{{ loading.search ? "Aguarde..." : "Pesquisar" }}</span>
            </b-button>
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>

    <b-card no-body class="mb-0">
      <div class="m-2">
        <b-row>
          <b-col cols="12" md="6" class="d-flex align-items-center justify-content-start mb-1 mb-md-0">
            <label class="mb-0">Exibir</label>
            <v-select
              v-model="itemsPerPage"
              dir="ltr"
              :options="itemsPerPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block mx-50"
              @input="search(1)"
            >
              <template #no-options>
                Sem resultados
              </template>
            </v-select>
            <label class="mb-0">itens</label>
          </b-col>
          <b-col cols="12" md="6" class="text-right">
            <b-button
              variant="outline-primary"
              :disabled="loading.export"
              @click.prevent="exportData()"
            >
              <b-spinner v-if="loading.export" small />
              <feather-icon v-else icon="ArrowDownCircleIcon" class="mr-50" />
              <span>{{ loading.export ? "Aguarde..." : "Exportar planilha" }}</span>
            </b-button>
          </b-col>
        </b-row>
      </div>
      <b-table
        hover
        small
        responsive
        table-class="text-nowrap"
        :items="items"
        :fields="tableColumns"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        :no-local-sorting="isFaturometro"
        show-empty
        empty-text="Nenhum registro encontrado para este período"
        :busy="loading.search"
        @sort-changed="currentPage = 1"
      />
      <div class="mx-2 mb-2">
        <b-row>
          <b-col cols="12" sm="6" class="d-flex align-items-center justify-content-center justify-content-sm-start">
            <span class="text-muted">
              Exibindo {{ pageItemsFrom }} a {{ pageItemsTo }} de {{ totalItems }} itens
            </span>
          </b-col>
          <b-col cols="12" sm="6" class="d-flex align-items-center justify-content-center justify-content-sm-end">
            <b-pagination
              :value="currentPage"
              :total-rows="totalItems"
              :per-page="itemsPerPage"
              first-number
              last-number
              class="mb-0 mt-1 mt-sm-0"
              @change="search"
            />
          </b-col>
        </b-row>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BButton,
  BTable,
  BFormGroup,
  BFormSelect,
  BPagination,
  BCardHeader,
  BCardBody,
  BSpinner,
} from "bootstrap-vue";
import vSelect from "vue-select";
import { mapActions, mapGetters } from "vuex";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent";
import PeriodRangePicker from "@/modules/shared/components/PeriodRangePicker";
import { getRangeDates } from "@/helpers/date_picker";
import * as sharedTypes from "@/modules/shared/store/types";
import * as reportTypes from "../store/types";
import * as kpiTypes from "@/modules/kpi/store/types";
import { addTotals, emptyTotals, indexStructure } from "@/modules/kpi/utils/kpiTree";
import { DEFAULT_COLUMN_KEYS, resolveColumns } from "@/modules/kpi/utils/kpiColumns";

// Campos de cada relatório, na ordem das colunas do export do backend.
const APPOINTMENTS_FIELDS = [
  ["id_franquia_digital", "Id franquia digital"], ["id_agendamento", "Id agendamento"],
  ["dt_reporte", "Data reporte"], ["dt_criacao_agendamento", "Criação agendamento"],
  ["cliente", "Cliente"], ["email", "E-mail"], ["compromisso", "Compromisso"],
  ["dt_compromisso", "Data compromisso"], ["Horario", "Horário"], ["status", "Status"],
  ["id_consultor", "Id consultor"], ["consultor", "Consultor"], ["franquia", "Franquia"],
  ["nivel", "Nível"], ["consultor_status", "Status consultor"],
  ["consultor_especialista", "Consultor especialista"], ["id_consultor_especialista", "Id especialista"],
  ["consultor_especialista_status", "Status especialista"], ["physical_franchise_name", "Franquia física"],
  ["houve_reuniao", "Houve reunião"], ["fechou_negocio", "Fechou negócio"], ["porque", "Porque"],
  ["feedback", "Feedback"], ["Ano", "Ano"], ["Semana", "Semana"], ["Mes", "Mês"], ["AnoMesFormatado", "Ano-mês"],
];

const INVOICING_FIELDS = [
  ["cod_contrato", "Cód. contrato"], ["contract_date", "Data do contrato"], ["valor", "Valor"],
  ["status_code", "Status código"], ["status_name", "Status"], ["solucao_financeira", "Solução financeira"],
  ["empresa", "Empresa"], ["id_consultor", "Id consultor"], ["consultor", "Consultor"],
  ["data_aprovacao_pn", "Data aprovação PN"], ["created_at", "Criado em"], ["deleted_at", "Excluído em"],
  ["physical_franchise_id", "Franquia física id"], ["digital_franchise_id", "Franquia digital id"],
  ["leader_physical_name", "Líder físico"], ["leader_franchise_name", "Franquia digital"],
  ["renewal_version", "Renovação"], ["nivel", "Nível"],
];

// O Faturômetro usa as mesmas colunas padrão da tela do Faturômetro 2.0, com o
// mesmo cálculo (kpiColumns.js), para os números baterem com ela.
const FATUROMETRO_COLUMNS = resolveColumns(DEFAULT_COLUMN_KEYS);

const FIELDS_BY_TYPE = {
  entries: [["pn_name", "PN"], ["franchise_name", "Franquia"], ["registered_at", "Data de cadastro no Oráculo"]],
  exits: [["pn_name", "PN"], ["franchise_name", "Franquia"], ["reason_name", "Motivo da saída"], ["terminated_at", "Data da saída"]],
  appointments: APPOINTMENTS_FIELDS,
  invoicing: INVOICING_FIELDS,
};

// Ordena texto por ordem alfabética e número por valor; o que não é número
// finito (ex.: divisão por zero) vai para o fim, como o "—" da tela.
const compareValues = (a, b) => {
  if (typeof a === "string" || typeof b === "string") {
    return String(a || "").localeCompare(String(b || ""));
  }
  const x = Number.isFinite(a) ? a : -Infinity;
  const y = Number.isFinite(b) ? b : -Infinity;
  if (x === y) {
    return 0;
  }
  return x < y ? -1 : 1;
};

const pad = n => String(n).padStart(2, "0");
const formatDate = d => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

export default {
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BRow,
    BCol,
    BButton,
    BTable,
    BFormGroup,
    BFormSelect,
    BPagination,
    BSpinner,
    vSelect,
    PeriodRangePicker,
  },
  setup() {
    return { toast: useToast() };
  },
  data() {
    const today = new Date();
    return {
      loading: {
        search: false,
        export: false,
      },
      reportTypes: [
        { value: "appointments", text: "Ranking de compromissos" },
        { value: "invoicing", text: "Ranking de faturamento" },
        { value: "faturometro", text: "Faturômetro" },
        { value: "entries", text: "Entrada de PNs" },
        { value: "exits", text: "Saída de PNs" },
      ],
      type: "entries",
      // Mesmo formato que o flat-pickr em modo range devolve: "início até fim".
      period: `${formatDate(new Date(today.getFullYear(), today.getMonth(), 1))} até ${formatDate(today)}`,
      currentPage: 1,
      itemsPerPage: 1,
      // Linhas do Faturômetro já calculadas; a paginação dele é feita aqui.
      faturometroRows: [],
      // Período ("início|fim") das linhas carregadas acima.
      faturometroSignature: null,
      // Ordenação do Faturômetro, aplicada na lista inteira e não só na página.
      sortBy: "franchise_name",
      sortDesc: false,
    };
  },
  computed: {
    ...mapGetters({
      report: reportTypes.SALESOPS_REPORT,
      initialItemsPerPage: sharedTypes.INITIAL_ITEMS_PER_PAGE,
      itemsPerPageOptions: sharedTypes.ITEMS_PER_PAGE_OPTIONS,
    }),
    tableColumns() {
      if (this.isFaturometro) {
        return [
          { key: "pn_name", label: "PN", sortable: true, stickyColumn: true },
          { key: "role_name", label: "Cargo", sortable: true },
          { key: "franchise_name", label: "Franquia", sortable: true },
          ...FATUROMETRO_COLUMNS.map(column => ({
            key: column.key,
            label: column.label,
            sortable: true,
            thClass: "text-right",
            // Zero em cinza, como na tela do Faturômetro: destaca quem produziu.
            tdClass: value => (value ? "text-right" : "text-right text-muted"),
            formatter: value => this.formatFaturometroValue(column, value),
          })),
        ];
      }
      return (FIELDS_BY_TYPE[this.type] || []).map(([key, label]) => ({
        key,
        label,
        sortable: false,
        stickyColumn: key === "pn_name",
      }));
    },
    sortedFaturometroRows() {
      const key = this.sortBy || "franchise_name";
      const direction = this.sortDesc ? -1 : 1;
      return [...this.faturometroRows].sort((a, b) => compareValues(a[key], b[key]) * direction
        || a.pn_name.localeCompare(b.pn_name));
    },
    isFaturometro() {
      return this.type === "faturometro";
    },
    items() {
      if (this.isFaturometro) {
        const start = (this.currentPage - 1) * this.itemsPerPage;
        return this.sortedFaturometroRows.slice(start, start + this.itemsPerPage);
      }
      return this.report?.data || [];
    },
    totalItems() {
      return this.isFaturometro ? this.faturometroRows.length : this.report?.total || 0;
    },
    pageItemsFrom() {
      if (this.isFaturometro) {
        return this.totalItems ? (this.currentPage - 1) * this.itemsPerPage + 1 : 0;
      }
      return this.report?.from || 0;
    },
    pageItemsTo() {
      if (this.isFaturometro) {
        return Math.min(this.currentPage * this.itemsPerPage, this.totalItems);
      }
      return this.report?.to || 0;
    },
  },
  mounted() {
    this.itemsPerPage = this.initialItemsPerPage;
    this.search(1);
  },
  methods: {
    ...mapActions({
      searchSalesOps: reportTypes.SEARCH_SALESOPS_REPORT,
      exportSalesOps: reportTypes.EXPORT_SALESOPS_REPORT,
      getKpiTable: kpiTypes.GET_CONSULTANT_AND_CHILDS,
      getKpiStructure: kpiTypes.GET_CONSULTANT_STRUCTURE,
    }),
    payload() {
      const { start, end } = getRangeDates(this.period);
      return {
        type: this.type,
        startDate: start,
        endDate: end,
        limit: this.itemsPerPage,
      };
    },
    search(page = 1) {
      const { startDate, endDate } = this.payload();
      if (!startDate || !endDate) {
        this.showError("Selecione a data inicial e a data final do período.");
        return;
      }
      if (this.isFaturometro) {
        this.searchFaturometro(page, startDate, endDate);
        return;
      }
      this.currentPage = page;
      this.loading.search = true;
      this.searchSalesOps({ ...this.payload(), currentPage: page })
        .catch(() => this.showError("Ocorreu um erro ao consultar o relatório."))
        .finally(() => {
          this.loading.search = false;
        });
    },
    exportData() {
      const { startDate, endDate } = this.payload();
      if (!startDate || !endDate) {
        this.showError("Selecione a data inicial e a data final do período.");
        return;
      }
      if (this.isFaturometro) {
        this.exportFaturometro();
        return;
      }
      this.loading.export = true;
      this.exportSalesOps(this.payload())
        .catch(() => this.showError("Ocorreu um erro ao exportar o relatório."))
        .finally(() => {
          this.loading.export = false;
        });
    },
    /**
     * O Faturômetro vem do backend-kpi, pelas mesmas rotas da tela do
     * Faturômetro 2.0 (/table para os números, /structure para o cargo) e com
     * os filtros padrão dela. Uma linha por PN e franquia, com a produção
     * própria do PN. Trocar de página não busca de novo: só recorta a lista.
     */
    searchFaturometro(page, startDate, endDate) {
      const signature = `${startDate}|${endDate}`;
      if (page > 1 && signature === this.faturometroSignature) {
        this.currentPage = page;
        return;
      }
      this.currentPage = 1;
      this.loading.search = true;
      const filters = {
        includeInactiveFranchises: false,
        includeInactiveNetworks: false,
      };
      Promise.all([
        this.getKpiTable({
          ...filters,
          startDate,
          endDate,
          kpiContractStatus: "valid",
          snapshotMode: "live",
          allConsultants: false,
          disableContractNetworks: false,
          includeEmployees: false,
          financialSolutionIds: [],
          partnerCompanyIds: [],
          skipCommit: true,
        }),
        this.getKpiStructure({ ...filters, asOf: endDate }),
      ])
        .then(([table, structure]) => {
          const index = indexStructure(structure.data?.nodes || []);
          this.faturometroRows = (table.data || []).map(row => {
            const totals = addTotals(emptyTotals(), row);
            const node = index.get(row.consultant_id || row.id, row.digital_franchise_id);
            const item = {
              pn_name: row.name || row.consultant_name || "",
              role_name: node?.consultant_role_name || "",
              franchise_name: row.leader_franchise_name || `Franquia ${row.digital_franchise_id}`,
            };
            FATUROMETRO_COLUMNS.forEach(column => {
              item[column.key] = column.value(totals);
            });
            return item;
          });
          this.faturometroSignature = signature;
        })
        .catch(() => {
          this.faturometroRows = [];
          this.faturometroSignature = null;
          this.showError("Ocorreu um erro ao consultar o Faturômetro.");
        })
        .finally(() => {
          this.loading.search = false;
        });
    },
    // Mesma formatação da célula do Faturômetro (KpiTreeTable.format).
    formatFaturometroValue(column, value) {
      if (!Number.isFinite(value)) {
        return "—";
      }
      const { filters } = this.$options;
      if (column.type === "money") {
        return filters.toCurrency(value);
      }
      if (column.type === "percent") {
        return filters.toPercentage(value);
      }
      if (column.type === "decimal") {
        return filters.toDecimal(value);
      }
      return value;
    },
    /**
     * O CSV no mesmo formato do botão de exportar do Faturômetro: ponto e
     * vírgula, vírgula decimal e BOM, para o Excel em português abrir certo.
     * Exporta todas as linhas do período, não só a página, na ordem da tela.
     */
    exportFaturometro() {
      if (!this.faturometroRows.length) {
        this.showError("Pesquise o período antes de exportar.");
        return;
      }
      const separator = ";";
      const number = value => (Number.isFinite(value)
        ? String(Math.round(value * 100) / 100).replace(".", ",")
        : "");
      const text = value => {
        const raw = String(value === null || value === undefined ? "" : value);
        return /[;"\r\n]/.test(raw) ? `"${raw.replace(/"/g, '""')}"` : raw;
      };
      const lines = [
        ["PN", "Cargo", "Franquia", ...FATUROMETRO_COLUMNS.map(column => column.label)]
          .map(text).join(separator),
        ...this.sortedFaturometroRows.map(row => [
          text(row.pn_name),
          text(row.role_name),
          text(row.franchise_name),
          ...FATUROMETRO_COLUMNS.map(column => number(row[column.key])),
        ].join(separator)),
      ];
      const blob = new Blob([String.fromCharCode(0xFEFF), lines.join("\r\n")], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `salesops_faturometro_${this.faturometroSignature.replace("|", "_")}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
    showError(text) {
      this.toast({
        component: ToastificationContent,
        props: { title: text, icon: "AlertTriangleIcon", variant: "danger" },
      });
    },
  },
};
</script>
